import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Trophy, Crown, Star, Award } from "lucide-react";

import SidebarLayout from "@/components/SidebarLayout";
import { api } from "@/lib/api";


const TIER_META = {
    novice: { color: "#CCCCCC", icon: Star },
    pro: { color: "#FFFFFF", icon: Trophy },
    legend: { color: "#CCCCCC", icon: Crown },
    mythic: { color: "#FAFAFA", icon: Crown },
};


export default function CivilizationLeaderboard() {
    const [scope, setScope] = useState("era");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await api.civilizationLeaderboard(scope, 100);
            setData(d);
        } catch (e) {
            toast.error(e.message || "Failed to load");
        } finally {
            setLoading(false);
        }
    }, [scope]);
    useEffect(() => {
        load();
    }, [load]);

    return (
        <SidebarLayout>
            <div className="max-w-4xl mx-auto py-6 px-2">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-semibold">Leaderboard</h1>
                        <p className="text-sm text-text-secondary">
                            {data?.total || 0} public members. Rispetta chi ha opted out.
                        </p>
                    </div>
                    <div className="flex gap-2" data-testid="lb-scope-tabs">
                        <ScopeBtn
                            active={scope === "era"}
                            onClick={() => setScope("era")}
                            label="This Era"
                            testid="lb-scope-era"
                        />
                        <ScopeBtn
                            active={scope === "all"}
                            onClick={() => setScope("all")}
                            label="All Time"
                            testid="lb-scope-all"
                        />
                    </div>
                </div>

                {loading || !data ? (
                    <p className="text-sm text-text-secondary">Loading…</p>
                ) : (
                    <>
                        {data.my_rank && data.my_rank > 100 && (
                            <div
                                data-testid="lb-my-rank-outside"
                                className="mb-4 rounded-xl border border-[var(--color-primary)]/30 bg-[var(--color-primary)]/10 p-3 text-sm"
                            >
                                Il tuo rank: <strong>#{data.my_rank}</strong>. Non sei nella top 100
                                ancora.
                            </div>
                        )}
                        <div
                            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden"
                            data-testid="lb-table"
                        >
                            <table className="min-w-full text-sm">
                                <thead>
                                    <tr className="text-left text-[10px] uppercase tracking-widest text-text-secondary border-b border-[var(--color-border)]">
                                        <th className="px-4 py-3 w-16">Rank</th>
                                        <th className="px-4 py-3">Member</th>
                                        <th className="px-4 py-3">Tier</th>
                                        <th className="px-4 py-3 text-right">
                                            {scope === "era" ? "Era Score" : "Total Score"}
                                        </th>
                                        <th className="px-4 py-3 text-right">Badges</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.items.map((it) => {
                                        const isMe = it.rank === data.my_rank;
                                        const meta = TIER_META[it.tier] || TIER_META.novice;
                                        const TierIcon = meta.icon;
                                        return (
                                            <tr
                                                key={it.user_id}
                                                data-testid={`lb-row-${it.rank}`}
                                                className={`border-b border-[var(--color-border)] last:border-0 ${
                                                    isMe ? "bg-[var(--color-primary)]/8" : ""
                                                }`}
                                            >
                                                <td className="px-4 py-3 tabular-nums font-medium">
                                                    #{it.rank}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <Link
                                                        to={`/civilization/profile/${it.user_id}`}
                                                        className="hover:underline"
                                                    >
                                                        {it.name}
                                                    </Link>
                                                    {isMe && (
                                                        <span className="ml-2 text-[10px] text-[var(--color-primary)] uppercase tracking-widest">
                                                            You
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span
                                                        className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest"
                                                        style={{ color: meta.color }}
                                                    >
                                                        <TierIcon className="h-3 w-3" /> {it.tier}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 tabular-nums text-right">
                                                    {(scope === "era"
                                                        ? it.era_score
                                                        : it.score) || 0}
                                                </td>
                                                <td className="px-4 py-3 tabular-nums text-right">
                                                    <span className="inline-flex items-center gap-1 text-text-secondary text-xs">
                                                        <Award className="h-3 w-3" />
                                                        {it.badges_count}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        </SidebarLayout>
    );
}

function ScopeBtn({ active, onClick, label, testid }) {
    return (
        <button
            data-testid={testid}
            onClick={onClick}
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs uppercase tracking-widest ${
                active
                    ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                    : "border-[var(--color-border)] text-text-secondary hover:text-text-main"
            }`}
        >
            {label}
        </button>
    );
}
